import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wrench, Loader2, SearchCheck, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function OrderStatusRepair() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  const authHeaders = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

  const runRepair = async (dryRun) => {
    const setBusy = dryRun ? setPreviewing : setApplying;
    setBusy(true);
    try {
      const res = await axios.post(
        API + '/admin/dc-order-sync/repair-orphan-dispatch?dry_run=' + dryRun,
        {},
        { headers: authHeaders() }
      );
      if (dryRun) {
        setPreview(res.data);
        setApplied(null);
        toast.info(res.data.lines_repaired + ' order line(s) need repair');
      } else {
        setApplied(res.data);
        setPreview(null);
        toast.success('Repaired ' + res.data.lines_repaired + ' line(s) across ' + (res.data.orders_touched || []).length + ' order(s)');
      }
    } catch (err) {
      toast.error(err.response && err.response.data ? String(err.response.data.detail) : 'Repair failed');
    }
    setBusy(false);
  };

  const repairs = (preview && preview.repairs) || (applied && applied.repairs) || [];

  return (
    <Card data-testid="order-status-repair-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Wrench className="w-5 h-5 text-blue-600" />
          Repair Order Statuses
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">
          Fixes franchise order lines wrongly marked <b>Dispatched / Partially Dispatched</b> by an old
          Delivery Challan that was dispatched <i>before</i> the order was even created (or whose DC no longer exists).
          Affected lines are reset to Ready to Pick / Pending to Procurement. Run <b>Preview</b> first, then <b>Apply</b>.
        </p>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => runRepair(true)}
            disabled={previewing || applying}
            className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="repair-preview-btn"
          >
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchCheck className="w-4 h-4" />}
            {previewing ? 'Scanning...' : 'Preview (Dry Run)'}
          </Button>
          <Button
            onClick={() => runRepair(false)}
            disabled={previewing || applying || !preview || preview.lines_repaired === 0}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            data-testid="repair-apply-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            {applying ? 'Repairing...' : 'Apply Repair'}
          </Button>
        </div>

        {preview && preview.lines_repaired === 0 && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="repair-clean-msg">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">No corrupted order lines found — all statuses are healthy.</span>
          </div>
        )}

        {applied && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="repair-applied-msg">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">
              Repaired {applied.lines_repaired} line(s). Orders: {(applied.orders_touched || []).join(', ') || '—'}
            </span>
          </div>
        )}

        {preview && preview.lines_repaired > 0 && (
          <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="repair-preview-table">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">{preview.lines_repaired} line(s) will be repaired</span>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y">
              {repairs.map((r, i) => (
                <div key={i} className="px-3 py-2 text-xs flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="font-mono">{r.order_number}</Badge>
                  <span className="font-medium">{r.product_name}</span>
                  <span className="text-slate-400">({r.product_id})</span>
                  <Badge className="bg-red-100 text-red-700">{r.was_status}</Badge>
                  <span className="text-slate-400">→</span>
                  <Badge className="bg-green-100 text-green-700">{r.will_be_status}</Badge>
                  <span className="text-slate-500 italic">{r.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default OrderStatusRepair;
